/**
 * Shared address, region, and category section for website forms.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  AddressAutocomplete,
  type AddressAutocompleteStatus,
} from "./AddressAutocomplete";
import {
  RegionSelector,
  type RegionSelectorLabels,
} from "../website-suggest/RegionSelector";
import { CategoryCombobox } from "../website-suggest/CategoryCombobox";
import {
  guessRegionByZipCode,
  suggestCategoryByAi,
} from "@/services/websiteSuggestion.service";
import { getAddressAutocompleteEnabled } from "@/services/publicSystemSettings.service";
import {
  logGeocodingEvent,
  type GeocodingConfidence,
  type GeocodingSuggestion,
  type GeocodingRegionType,
  type GeocodingRegion,
} from "@/services/geocoding.service";

type CategorizationFields = {
  title: string;
  url: string;
  description: string;
  address: string;
  zipCode: string;

  category1Id: string | null;
  category1Name: string;
  category1Depth?: number | null;
  categoryPathIds: string[];
  categoryPathNames: string[];

  regionId: string | null;
  regionName: string;
  regionPathIds: string[];
  regionPathNames: string[];
};

interface WebsiteCategorizationFieldsProps<T extends CategorizationFields> {
  formData: T;
  setFormData: React.Dispatch<React.SetStateAction<T>>;
  t: (key: string) => string;
  language: string;
  addressInputId: string;
  maxTitleLen: number;
  maxDescriptionLen: number;
  maxAddressLen: number;
  maxZipLen: number;
  isSubmitting?: boolean;
  aiFeatureEnabled?: boolean;
  primaryTextStyle?: CSSProperties;
  secondaryTextStyle?: CSSProperties;
  errorTextStyle?: CSSProperties;
  categoryLabelText?: string;
  categoryFieldId?: string;
  regionFieldId?: string;
  showCategoryRequiredError?: boolean;
  showRegionRequiredError?: boolean;
  categoryRequiredErrorText?: string;
  regionRequiredErrorText?: string;
  addressLabelText?: string;
  regionLabels?: RegionSelectorLabels;
  resetKey?: number;
}

export type WebsiteCategorizationLabels = RegionSelectorLabels;

export function WebsiteCategorizationFields<T extends CategorizationFields>({
  formData,
  setFormData,
  t,
  language,
  addressInputId,
  maxTitleLen,
  maxDescriptionLen,
  maxAddressLen,
  maxZipLen,
  isSubmitting = false,
  aiFeatureEnabled = true,
  primaryTextStyle,
  secondaryTextStyle,
  errorTextStyle,
  categoryLabelText,
  categoryFieldId,
  regionFieldId,
  showCategoryRequiredError = false,
  showRegionRequiredError = false,
  categoryRequiredErrorText,
  regionRequiredErrorText,
  addressLabelText,
  regionLabels,
  resetKey,
}: WebsiteCategorizationFieldsProps<T>) {
  const [openCategory1, setOpenCategory1] = useState(false);
  const [openRegion, setOpenRegion] = useState(false);
  const [autoConfidence, setAutoConfidence] =
    useState<GeocodingConfidence | null>(null);
  const [autoStatus, setAutoStatus] =
    useState<AddressAutocompleteStatus>("idle");
  const [aiLoading, setAiLoading] = useState(false);
  const [wasAddressTooLong, setWasAddressTooLong] = useState(false);
  const [wasZipTooLong, setWasZipTooLong] = useState(false);
  const [autoRegionApplied, setAutoRegionApplied] = useState(false);
  const [autoRegionType, setAutoRegionType] =
    useState<GeocodingRegionType | null>(null);
  const [addressAutocompleteEnabled, setAddressAutocompleteEnabled] =
    useState(true);

  useEffect(() => {
    let isMounted = true;
    const loadSetting = async () => {
      const enabled = await getAddressAutocompleteEnabled();
      if (isMounted) setAddressAutocompleteEnabled(enabled);
    };
    loadSetting();
    return () => {
      isMounted = false;
    };
  }, []);

  const resetLocalState = useCallback(() => {
    setOpenCategory1(false);
    setOpenRegion(false);
    setAutoConfidence(null);
    setAutoRegionType(null);
    setAutoStatus("idle");
    setAiLoading(false);
    setWasAddressTooLong(false);
    setWasZipTooLong(false);
    setAutoRegionApplied(false);
  }, []);

  useEffect(() => {
    resetLocalState();
  }, [resetKey, resetLocalState]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      category1Id: null,
      category1Name: "",
      category1Depth: null,
      categoryPathIds: [],
      categoryPathNames: [],
    }));

    setAiLoading(false);

    setOpenCategory1(false);
  }, [language, setFormData]);

  useEffect(() => {
    if (aiFeatureEnabled) return;
    setAiLoading(false);
  }, [aiFeatureEnabled]);

  const clearRegionSelection = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      regionId: null,
      regionName: "",
      regionPathIds: [],
      regionPathNames: [],
    }));
  }, [setFormData]);

  useEffect(() => {
    const z = (formData.zipCode ?? "").trim();
    const hasRegionSelection =
      Boolean(formData.regionId) ||
      Boolean(formData.regionName) ||
      (formData.regionPathIds?.length ?? 0) > 0 ||
      (formData.regionPathNames?.length ?? 0) > 0;

    if (hasRegionSelection && !autoRegionApplied) {
      return;
    }

    if (!z) {
      setAutoRegionApplied(false);
      return;
    }

    if (!/^\d{5}$/.test(z)) {
      if (autoRegionApplied) {
        clearRegionSelection();
        setAutoRegionApplied(false);
      }
      return;
    }

    const timer = window.setTimeout(async () => {
      const res = await guessRegionByZipCode(z);

      if (!res.ok) return;

      if (!res.regionId) {
        if (autoRegionApplied) {
          clearRegionSelection();
          setAutoRegionApplied(false);
        }
        return;
      }

      setFormData((prev) => {
        const userHasManualRegion = Boolean(prev.regionId) && !autoRegionApplied;
        if (userHasManualRegion) return prev;

        const name =
          res.regionName && res.regionName.trim() ? res.regionName.trim() : "";

        return {
          ...prev,
          regionId: res.regionId,
          regionName: name,
          regionPathIds: [res.regionId],
          regionPathNames: name ? [name] : [],
        };
      });

      setAutoRegionApplied(true);
    }, 150);

    return () => window.clearTimeout(timer);
  }, [
    autoRegionApplied,
    clearRegionSelection,
    formData.regionId,
    formData.regionName,
    formData.regionPathIds,
    formData.regionPathNames,
    formData.zipCode,
    setFormData,
  ]);

  const handleAutoSelect = useCallback(
    (suggestion: GeocodingSuggestion) => {
      const resolveRegionType = (region: GeocodingRegion | null) => {
        if (!region) return null;
        if (region.type) return region.type;
        const depth = Math.max(region.pathIds.length, region.pathNames.length);
        if (depth >= 4) return "POSTAL_CODE";
        if (depth === 3) return "DISTRICT";
        if (depth === 2) return "ADMINISTRATIVE_DISTRICT";
        if (depth === 1 || region.id) return "STATE";
        return null;
      };

      setAutoStatus("idle");
      setAutoConfidence(suggestion.confidence);
      setAutoRegionApplied(false);
      setAutoRegionType(resolveRegionType(suggestion.region));
      void logGeocodingEvent({
        event: "resolution_selected",
        confidence: suggestion.confidence,
      });

      setFormData((prev) => {
        const next = {
          ...prev,
          address: suggestion.label,
          zipCode: suggestion.postalCode ?? "",
        };
        if (suggestion.region) {
          const pathIds =
            suggestion.region.pathIds.length > 0
              ? suggestion.region.pathIds
              : [suggestion.region.id];
          const pathNames =
            suggestion.region.pathNames.length > 0
              ? suggestion.region.pathNames
              : [suggestion.region.name];

          next.regionId = suggestion.region.id;
          next.regionName = suggestion.region.name;
          next.regionPathIds = pathIds;
          next.regionPathNames = pathNames;
        } else {
          next.regionId = null;
          next.regionName = "";
          next.regionPathIds = [];
          next.regionPathNames = [];
        }
        return next;
      });

      if (!suggestion.region) {
        setAutoStatus("invalid_response");
      }
    },
    [setFormData]
  );

  const lastLoggedStatusRef = useRef<AddressAutocompleteStatus>("idle");
  useEffect(() => {
    if (autoStatus === "idle" || autoStatus === "no_results") {
      lastLoggedStatusRef.current = autoStatus;
      return;
    }
    if (lastLoggedStatusRef.current === autoStatus) return;

    lastLoggedStatusRef.current = autoStatus;
    void logGeocodingEvent({
      event: "autocomplete_status",
      reason: autoStatus,
    });
  }, [autoStatus]);

  const handleZipCodeChange = useCallback(
    (zip: string) => {
      const cleaned = zip.replace(/\D/g, "").slice(0, maxZipLen);

      setFormData((prev) => ({ ...prev, zipCode: cleaned }));
      setAutoRegionApplied(false);

      const tooLong = cleaned.length > maxZipLen;
      if (tooLong && !wasZipTooLong) {
        toast.error(
          t("websiteSuggest.errorZipTooLong").replace(
            "{max}",
            String(maxZipLen)
          )
        );
      }
      setWasZipTooLong(tooLong);
    },
    [maxZipLen, setFormData, t, wasZipTooLong]
  );

  const handleAiSuggest = useCallback(async () => {
    const titleEmpty = !formData.title.trim();
    const urlEmpty = !formData.url.trim();
    const descriptionEmpty = !formData.description.trim();

    if (titleEmpty || urlEmpty || descriptionEmpty) {
      toast.error(t("websiteSuggest.aiNeedTitleAndDescription"));
      return;
    }

    if (formData.title.length > maxTitleLen) {
      toast.error(
        t("websiteSuggest.errorTitleTooLong").replace(
          "{max}",
          String(maxTitleLen)
        )
      );
      return;
    }

    if (formData.description.length > maxDescriptionLen) {
      toast.error(
        t("websiteSuggest.errorDescriptionTooLong").replace(
          "{max}",
          String(maxDescriptionLen)
        )
      );
      return;
    }

    if (aiLoading) return;

    setAiLoading(true);
    try {
      const res = await suggestCategoryByAi({
        title: formData.title.trim(),
        description: formData.description.trim(),
        lang: language === "de" ? "de" : "en",
      });

      if (!res.ok) {
        switch (res.kind) {
          case "timeout":
            toast.error(t("websiteSuggest.aiTimeout"));
            break;
          case "bad_gateway":
            toast.error(t("websiteSuggest.aiBadGateway"));
            break;
          default:
            toast.error(t("websiteSuggest.aiFailed"));
            break;
        }
        return;
      }

      const data = res.data;

      const pathIds = Array.isArray(data.categoryPathIds)
        ? data.categoryPathIds
        : [];
      const pathNames = Array.isArray(data.categoryPathNames)
        ? data.categoryPathNames
        : [];

      const leafId =
        pathIds.length > 0 ? pathIds[pathIds.length - 1] : data.categoryId;
      const leafName =
        pathNames.length > 0
          ? pathNames[pathNames.length - 1]
          : data.categoryName;

      setFormData((prev) => ({
        ...prev,
        category1Id: leafId,
        category1Name: leafName,
        category1Depth: typeof data.depth === "number" ? data.depth : null,
        categoryPathIds: pathIds,
        categoryPathNames: pathNames,
      }));

      setOpenCategory1(false);

      toast.success(t("websiteSuggest.aiApplied"));
    } finally {
      setAiLoading(false);
    }
  }, [
    aiLoading,
    formData.description,
    formData.title,
    language,
    maxDescriptionLen,
    maxTitleLen,
    setFormData,
    t,
  ]);

  const addressTooLong = formData.address.length > maxAddressLen;
  const zipTooLong = formData.zipCode.length > maxZipLen;
  const aiMissingRequiredFields =
    !formData.title.trim() ||
    !formData.url.trim() ||
    !formData.description.trim();

  const confidenceLabel =
    autoConfidence === "EXACT"
      ? t("websiteSuggest.addressConfidenceExact")
      : autoConfidence === "AMBIGUOUS"
        ? t("websiteSuggest.addressConfidenceAmbiguous")
        : autoConfidence === "PARTIAL"
          ? autoRegionType === "STATE"
            ? t("websiteSuggest.addressConfidenceState")
            : autoRegionType === "ADMINISTRATIVE_DISTRICT"
              ? t("websiteSuggest.addressConfidenceAdminDistrict")
              : autoRegionType === "DISTRICT"
                ? t("websiteSuggest.addressConfidenceDistrict")
                : autoRegionType === "POSTAL_CODE"
                  ? t("websiteSuggest.addressConfidenceExact")
                  : t("websiteSuggest.addressConfidencePartial")
          : null;

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={addressInputId} style={primaryTextStyle}>
          {addressLabelText ?? t("websiteSuggest.addressLabel")}
        </Label>
        <AddressAutocomplete
          inputId={addressInputId}
          value={formData.address}
          onChange={(next) => {
            setFormData((prev) => ({ ...prev, address: next }));
            setAutoConfidence(null);
            if (addressAutocompleteEnabled) {
              setAutoStatus("idle");
            }
            setAutoRegionType(null);
            const tooLong = next.length > maxAddressLen;
            if (tooLong && !wasAddressTooLong) {
              toast.error(
                t("websiteSuggest.errorAddressTooLong").replace(
                  "{max}",
                  String(maxAddressLen)
                )
              );
            }
            setWasAddressTooLong(tooLong);
          }}
          onSelect={handleAutoSelect}
          onStatusChange={setAutoStatus}
          t={t}
          disabled={isSubmitting}
          suggestionsEnabled={addressAutocompleteEnabled}
        />

        {addressTooLong && (
          <p className="text-xs font-semibold" style={errorTextStyle}>
            {t("websiteSuggest.errorAddressTooLong").replace(
              "{max}",
              String(maxAddressLen)
            )}
          </p>
        )}

        {!addressAutocompleteEnabled && (
          <p className="text-xs text-secondary">
            {t("websiteSuggest.addressAutoDisabled")}
          </p>
        )}

        {addressAutocompleteEnabled &&
          (autoStatus === "unavailable" || autoStatus === "invalid_response") && (
          <p className="text-xs text-secondary">
            {t("websiteSuggest.addressAutoUnavailable")}
          </p>
        )}

        {addressAutocompleteEnabled && confidenceLabel && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" aria-live="polite">
              {confidenceLabel}
            </Badge>
          </div>
        )}

        {addressAutocompleteEnabled && (
          <p className="text-xs text-secondary">
            {t("websiteSuggest.addressAutoHint")}
          </p>
        )}
      </div>

      <div id={regionFieldId}>
        <RegionSelector
          formData={formData}
          setFormData={setFormData}
          openRegion={openRegion}
          setOpenRegion={setOpenRegion}
          onZipCodeChange={handleZipCodeChange}
          t={t}
          labels={regionLabels}
        />

        {zipTooLong && (
          <p className="text-xs font-semibold" style={errorTextStyle}>
            {t("websiteSuggest.errorZipTooLong").replace(
              "{max}",
              String(maxZipLen)
            )}
          </p>
        )}

        {showRegionRequiredError && regionRequiredErrorText && (
          <p className="text-xs font-semibold" style={errorTextStyle}>
            {regionRequiredErrorText}
          </p>
        )}
      </div>

      <div
        id={categoryFieldId}
        className="w-full sm:w-auto sm:flex-none min-w-0 sm:max-w-[280px]"
      >
        <CategoryCombobox
          formData={formData}
          setFormData={setFormData}
          openCategory1={openCategory1}
          setOpenCategory1={setOpenCategory1}
          t={t}
          language={language}
          disabled={isSubmitting}
          labelText={categoryLabelText}
          navigatorHintText={t("websiteSuggest.categoryNavigatorHint")}
          emptyStateAction={
            aiFeatureEnabled
              ? {
                  label: aiLoading
                    ? t("websiteSuggest.aiSuggesting")
                    : t("websiteSuggest.aiSuggestButton"),
                  onClick: () => {
                    void handleAiSuggest();
                  },
                  disabled: aiLoading || isSubmitting || aiMissingRequiredFields,
                  disabledTooltip: aiMissingRequiredFields
                    ? t("websiteSuggest.aiNeedTitleAndDescription")
                    : undefined,
                  icon: <Sparkles className="w-4 h-4" />,
                }
              : undefined
          }
        />

        {showCategoryRequiredError && categoryRequiredErrorText && (
          <p className="text-xs font-semibold" style={errorTextStyle}>
            {categoryRequiredErrorText}
          </p>
        )}
      </div>
    </>
  );
}
